import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  where,
  getDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";

import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function Department() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 3000,
    showConfirmButton: false
  });

  const [propertyId, setPropertyId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [department, setDepartment] = useState("");
  const [editId, setEditId] = useState("");
  const [editDepartment, setEditDepartment] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  /* ---------------- FETCH PROPERTY ID ---------------- */

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, "authroles", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const propId = docSnap.data().propertyId;
          setPropertyId(propId);
          fetchDepartments(propId);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  /* ---------------- FETCH DEPARTMENTS ---------------- */

  const fetchDepartments = (propId) => {
    const q = query(
      collection(db, "departments"),
      where("propertyId", "==", propId),
    );

    onSnapshot(q, (snapshot) => {
      setDepartments(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
      );
    });
  };

  /* ---------------- ADD ---------------- */

  const handleAdd = async () => {
    if (!department) {
      Toast.fire({ icon: "error", title: "Enter Department Name" });
      return;
    }

    await addDoc(collection(db, "departments"), {
      department,
      propertyId,
      createdAt: Timestamp.now()
    });

    setDepartment("");
    setOpenAdd(false);

    Toast.fire({ icon: "success", title: "Department Added" });
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    await deleteDoc(doc(db, "departments", id));

    Toast.fire({ icon: "success", title: "Deleted Successfully" });
  };

  /* ---------------- UPDATE ---------------- */

  const handleUpdate = async () => {
    if (!editDepartment) {
      Toast.fire({ icon: "error", title: "Enter Department Name" });
      return;
    }

    await updateDoc(doc(db, "departments", editId), {
      department: editDepartment
    });

    setOpenEdit(false);

    Toast.fire({ icon: "success", title: "Updated Successfully" });
  };

  /* ---------------- SEARCH ---------------- */

  const filteredData = departments.filter((item) =>
    item.department.toLowerCase().includes(searchText.toLowerCase())
  );

  /* ---------------- PAGINATION ---------------- */

  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  /* ---------------- UI ---------------- */

  return (
    <Box className="content-wrapper" sx={{ p: 4, backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Department Management
      </Typography>

      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <TextField
              label="Search Department"
              size="small"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
            />

            <Button
              variant="contained"
              onClick={() => setOpenAdd(true)}
            >
              Add Department
            </Button>
          </Box>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr No</TableCell>
                <TableCell>Department</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRecords.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {indexOfFirst + index + 1}
                  </TableCell>
                  <TableCell>{item.department}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setEditId(item.id);
                        setEditDepartment(item.department);
                        setOpenEdit(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => handleDelete(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>

                    <IconButton
                      component="a"
                      href={`/view-products/${item.id}`}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>
        </CardContent>
      </Card>

      {/* ADD DIALOG */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add Department</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Department"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Department"
            value={editDepartment}
            onChange={(e) => setEditDepartment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}